import { makeObservable, observable, action, runInAction } from 'mobx';

import { StockSubscriptionsRequests } from '~/api/StockSubscriptions';
import { company } from '~/company/Company';
import { CatalogStorePartial } from '~/stores/CatalogStore/interfaces';

import { Product } from '../CategoriesStore';
import { BaseStore } from '../interfaces/BaseStore';
import { orderStore } from '../OrderStore';
import { settingStore } from '../Settings';
import { MetaStore } from '../shared/MetaStore';
import { userStore } from '../UserStore';

export interface Pagination {
  total: number;
  page: number;
}

export interface StockSubscriptionSKUResponse {
  data: Map<number, string[]>;
}

export class NotifyMeStore implements BaseStore {
  private readonly _catalogStore: CatalogStorePartial;
  private readonly _abortController: AbortController = new AbortController();

  readonly notifyMeCRUDMeta: MetaStore = new MetaStore();
  readonly notifyMeProductListMeta: MetaStore = new MetaStore();
  readonly notifyMeSKUListMeta: MetaStore = new MetaStore();

  notifyMeProductList: Map<number, Product> = new Map();
  notifyMeSKUMap: Record<number, string[]> = {};

  gotSKUS = false;

  readonly notifyMeProductListPagination: Pagination = {
    total: 0,
    page: 1,
  };

  constructor(catalogStore: CatalogStorePartial) {
    this._catalogStore = catalogStore;

    makeObservable(this, {
      notifyMeProductList: observable,
      notifyMeSKUMap: observable,
      addNotifyMe: action.bound,
      deleteNotifyMe: action.bound,
    });
  }

  async addNotifyMe(addProductId: number, addSKU: string): Promise<boolean> {
    if (this.notifyMeCRUDMeta.isLoading) {
      return false;
    }

    this.notifyMeCRUDMeta.setLoading(true);

    try {
      const {
        data: { productId, sku },
      } = await StockSubscriptionsRequests.addSubscription(
        addProductId,
        addSKU,
        orderStore.etaCalculation?.warehouse.code ??
          company.config.warehouse.default,
        !!orderStore.etaCalculation?.warehouse?.availability?.deliveryMechanics
          ?.ON_DEMAND?.availableNow,
        settingStore.settings.backorderLogic,
        {
          signal: this._abortController.signal,
        },
      );
      if (this.notifyMeSKUMap[productId]) {
        this.notifyMeSKUMap[productId] = [
          ...this.notifyMeSKUMap[productId],
          sku,
        ];
      } else {
        this.notifyMeSKUMap[productId] = [sku];
      }
      return true;
    } catch (error) {
      error && console.error(error);
      return false;
    } finally {
      this.notifyMeCRUDMeta.setLoading(false);
    }
  }

  async deleteNotifyMe(
    deleteProductId: number,
    deleteSKU: string,
  ): Promise<boolean> {
    if (this.notifyMeCRUDMeta.isLoading) {
      return false;
    }

    this.notifyMeCRUDMeta.setLoading(true);

    try {
      await StockSubscriptionsRequests.deleteSubscription(
        deleteProductId,
        deleteSKU,
        orderStore.etaCalculation?.warehouse.code ??
          company.config.warehouse.default,
        {
          signal: this._abortController.signal,
        },
      );

      if (this.notifyMeSKUMap[deleteProductId]?.length === 1) {
        delete this.notifyMeSKUMap[deleteProductId];
      } else {
        this.notifyMeSKUMap[deleteProductId] = (
          this.notifyMeSKUMap[deleteProductId] || []
        )?.filter((sku) => sku !== deleteSKU);
      }
      return true;
    } catch (error) {
      error && console.error(error);
      return false;
    } finally {
      this.notifyMeCRUDMeta.setLoading(false);
    }
  }

  getNotifyMeSKUList = async (warehouseCode?: string): Promise<void> => {
    this.notifyMeSKUListMeta.setLoading(true);

    try {
      const { data = {} } =
        await StockSubscriptionsRequests.getSubscriptionsSKU(
          warehouseCode ??
            orderStore.etaCalculation?.warehouse.code ??
            company.config.warehouse.default,
        );

      if (data) {
        runInAction(() => {
          this.notifyMeSKUMap = data;
          this.gotSKUS = true;
        });
      }
    } catch (error) {
      error && console.error(error);
    } finally {
      this.notifyMeSKUListMeta.setLoading(false);
    }
  };

  getNotifyMeProductsList = async (
    warehouseCode?: string,
    isForce?: boolean,
  ): Promise<boolean> => {
    if (!userStore.isAuthorized) {
      return false;
    }

    try {
      if (isForce) {
        this.notifyMeProductListPagination.page = 1;
        this.notifyMeProductList = new Map<number, Product>();
      } else {
        this.notifyMeProductListPagination.page += 1;
      }

      const { data = [], pagination } =
        await StockSubscriptionsRequests.getSubscriptions(
          this.notifyMeProductListPagination.page,
          20,
          warehouseCode ??
            orderStore.etaCalculation?.warehouse.code ??
            company.config.warehouse.default,
        );

      runInAction(() => {
        data.forEach((product) => {
          this.notifyMeProductList.set(product.id, new Product(product));
        });

        this.notifyMeProductListPagination.page = pagination.current;
        this.notifyMeProductListPagination.total = pagination.total;
      });

      return data?.length > 0;
    } catch (e) {
      e && console.error(e);
    } finally {
      this.notifyMeSKUListMeta.setLoading(false);
    }

    return false;
  };

  get notifyMeItemsList(): Product[] {
    if (!this.notifyMeProductList.size) {
      return [];
    }
    return Array.from(this.notifyMeProductList, ([, value]) => value);
  }

  destroy() {
    this.notifyMeCRUDMeta.destroy();
    this.notifyMeProductListMeta.destroy();
    this.notifyMeSKUListMeta.destroy();
    this._abortController.abort();
  }
}
